// src/pages/admin/PrintSettings.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const PrintSettings = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    font_size: 16,
    margin_top: 20,
    display_patient_name: true,
    display_patient_age: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(API_URL + 'print-custom', {
          headers: { 'Accept': 'application/json' },
        });
        if (res && res.data) {
          const { font_size, margin_top, display_patient_name, display_patient_age } = res.data;
          setForm({
            font_size: Number.isInteger(font_size) ? font_size : 16,
            margin_top: Number.isInteger(margin_top) ? margin_top : 20,
            display_patient_name: display_patient_name !== undefined ? display_patient_name : true,
            display_patient_age: display_patient_age !== undefined ? display_patient_age : true,
          });
        }
      } catch (e) {
        setError('Failed to load print settings');
      } finally {
        setLoading(false);
      }
    };
    if (API_URL) fetchData();
  }, [API_URL]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value === '' ? '' : parseInt(value, 10) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {};
      if (form.font_size !== '' && !Number.isNaN(form.font_size)) payload.font_size = form.font_size;
      if (form.margin_top !== '' && !Number.isNaN(form.margin_top)) payload.margin_top = form.margin_top;
      payload.display_patient_name = form.display_patient_name;
      payload.display_patient_age = form.display_patient_age;

      const res = await axios.put(API_URL + 'print-custom', payload, {
        headers: { 'Accept': 'application/json' },
      });
      if (res && res.data) {
        setSuccess('Settings saved successfully');
      }
    } catch (e) {
      if (e.response && e.response.data && e.response.data.errors) {
        const messages = Object.values(e.response.data.errors).flat().join(' ');
        setError(messages || 'Failed to save settings');
      } else {
        setError('Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mt-4" style={{ direction: 'ltr', textAlign: 'left' }}>
      <div className="patient-list-card">
        <div className="patient-list-header">
          <h2 className="patient-list-title">
            <i className="fas fa-print me-2"></i>
            Print Settings
          </h2>
        </div>

        {loading ? (
          <div className="text-center my-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-3">
            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}
            {success && (
              <div className="alert alert-success" role="alert">
                <i className="fas fa-check-circle me-2"></i>
                {success}
              </div>
            )}
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label">Base Font Size (px)</label>
                <input
                  type="number"
                  min={8}
                  max={48}
                  className="form-control"
                  name="font_size"
                  value={form.font_size}
                  onChange={handleChange}
                  placeholder="Example: 16"
                  required
                />
                <small className="text-muted">Applied to PDF content. Allowed values 8-48 pixels.</small>
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label">Top Margin (mm)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="form-control"
                  name="margin_top"
                  value={form.margin_top}
                  onChange={handleChange}
                  placeholder="Example: 20"
                  required
                />
                <small className="text-muted">Affects the starting position of content on the page.</small>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="display_patient_name"
                    name="display_patient_name"
                    checked={form.display_patient_name}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="display_patient_name">
                    Display Patient Name on Prescription
                  </label>
                  <small className="text-muted d-block">Show or hide patient name when printing prescription.</small>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="display_patient_age"
                    name="display_patient_age"
                    checked={form.display_patient_age}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="display_patient_age">
                    Display Patient Age on Prescription
                  </label>
                  <small className="text-muted d-block">Show or hide patient age when printing prescription.</small>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 mt-4">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2"></i>
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PrintSettings;
